import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../types/stores";
import RadioButton from "../../material-components/Misc/RadioButton";
import { MenuItem, InputLabel, Button, FormControl, TextField, Select, Divider, Icon } from "@material-ui/core";
import { getArrayOfAllUsers } from "../../../reducers/users";
import type { User } from "../../../types/types";
import CircularLoading from "../../loading/CircularLoading";
import { addNewReassignedUser, requestNewUserBeforeReassign } from "../../../actions/userActions";
//import { getArrayOfAllRoles } from "../../../reducers/roles";
import { SetReassignItemsToUser } from "../../../actions/reassignedItemsToActions";
import h from "../../../helpers";
import { toJson } from "../../../helpers/typedHelpers";
import { request } from "../../../helpers/httpInterceptor";
import { UserDeleteActionData } from "../../../types/stores/adminDesigner";
import { numberOfFlows } from "../../../reducers/flows";

interface IPageErrors {
    Email?: string;
    FirstName?: string;
    LastName?: string;
    ExpireDate?: string;
}

interface IRole {
    Id: number;
    Name: string;
    Description: string;
    InternalOnly: boolean;
}

interface ISite {
    Id: number;
    Name: string;
    Description: string;
    URI: string;
}

/* eslint-disable camelcase */
interface IFeature {
    Id: number;
    Created: any;
    Name: string;
    Description: string;
    IsExternal: boolean;
    DefaultOn: boolean;
    IsClientFacing: boolean;
    v_CompanyTables: any;
    ApplicationUsers: any;
}

interface INewUser {
    Id: number;
    Email: string;
    FirstName: string;
    LastName: string;
    CurrentCompanyID: number;
}

export const defaultNewUser: INewUser = {
    Id: 0,
    CurrentCompanyID: 0,
    Email: "",
    FirstName: "",
    LastName: "",
};

interface IUserDeleteMain {
    userId: number;
    onChangeImportAction: (data: UserDeleteActionData) => void;
}

const UserDelete: React.FC<IUserDeleteMain> = ({ userId, onChangeImportAction }: IUserDeleteMain) => {
    const dispatch = useDispatch();

    const [reassignMenu, setReassignMenu] = useState("no");
    const [selectedUser, setSelectedUser] = useState(0);
    const [errors, setErrors] = useState<IPageErrors>({});
    const [newFolderName, setNewFolderName] = useState("");
    const [editNewUserMode, setEditNewUserMode] = useState(false);
    const [reassignMode, setReassignMode] = useState(true);

    const [userSites, setUserSites] = useState<Array<ISite>>([]);
    const [userRoles, setUserRoles] = useState<Array<IRole>>([]);
    const [defaultSite, setDefaultSite] = useState(false);
    const [userFeatures, setUserFeatures] = useState<Array<IFeature>>([]);

    const selectedCompany = useSelector((state: IAppState) => state.companyEdit);
    const currentCompanyId = useSelector((state: IAppState) => state.session.companyId);
    const sites = useSelector<IAppState, Array<ISite>>(state => state.sites);
    const adminRoles = useSelector((state: IAppState) => state.session.roles || []);
    const currentReassignedUser = useSelector((state: IAppState) => state.reassignedItemsTo.reassiendUserId || 0);
    const isSaving = useSelector<IAppState, boolean>(state => state.userAdmin.isSaving);

    const user = useSelector<IAppState, any>(state => state.users.byId[userId || 0]);
    const users = useSelector(state => getArrayOfAllUsers(state));
    const flows = useSelector(state => numberOfFlows(state, userId));
    const [localNewUser, setLocalNewUser] = useState({ ...defaultNewUser });

    useEffect(() => {
        if (flows.length <= 0) {
            setReassignMode(false);
        } else {
            setReassignMode(true);
        }
    }, [dispatch, flows]);

    useEffect(() => {
        setSelectedUser(currentReassignedUser);
    }, [dispatch, currentReassignedUser]);

    useEffect(() => {
        const defaultAction: UserDeleteActionData = {
            IsReassignedTo: selectedUser > 0 ? true : false,
            ReassignedFolderName: newFolderName.length > 0 ? newFolderName : "",
            IsShowPrimaryButton: editNewUserMode && currentReassignedUser == 0 ? false : true,
        };
        onChangeImportAction(defaultAction);
    }, [onChangeImportAction, selectedUser, newFolderName, editNewUserMode, currentReassignedUser]);

    const handleSettingValue = val => {
        setSelectedUser(val);
        dispatch(SetReassignItemsToUser(val));

        const defaultAction: UserDeleteActionData = {
            IsReassignedTo: true,
            ReassignedFolderName: newFolderName,
            IsShowPrimaryButton: true,
        };
        onChangeImportAction(defaultAction);
    };

    const handleReassignValue = val => {
        setReassignMenu(val);
        dispatch(SetReassignItemsToUser(0));
        setSelectedUser(0);
        if (val === "no") {
            const defaultAction: UserDeleteActionData = {
                IsReassignedTo: false,
                ReassignedFolderName: newFolderName,
                IsShowPrimaryButton: true,
            };
            onChangeImportAction(defaultAction);
        } else {
            const defaultAction2: UserDeleteActionData = {
                IsReassignedTo: true,
                ReassignedFolderName: newFolderName,
                IsShowPrimaryButton: false,
            };
            onChangeImportAction(defaultAction2);
        }
    };

    const handleFocus = event => event.target.select();
    // Set default site for new User

    useEffect(() => {
        if (userId <= 0 && sites.length > 0 && !userSites.find(x => x.Name == "AudienceEngine") && !defaultSite) {
            const site = sites.find(x => x.Name == "AudienceEngine");

            if (site) {
                const newUserSite = [...userSites];
                const userSite: ISite = { ...site };
                newUserSite.push(userSite);
                setDefaultSite(true);
                setUserSites(newUserSite);
            }
        }
    }, [userId, userSites, sites, defaultSite, dispatch]);

    useEffect(() => {
        if ((adminRoles.includes("Super Admin") || adminRoles.includes("Experian Admin")) && userId && userId != 0) {
            fetch(`/api/roles/user/${userId}`, { credentials: "same-origin" })
                .then(h.checkStatus)
                .then(toJson)
                .then(data => {
                    const response = data as unknown as {
                        roles: Array<IRole>;
                    };
                    setUserRoles(response.roles);
                })
                .catch(error => {
                    h.error("Error loading User Roles.", error);
                });
        }
    }, [userId, adminRoles]);

    // Get User Details
    useEffect(() => {
        if ((adminRoles.includes("Super Admin") || adminRoles.includes("Experian Admin")) && userId > 0) {
            const getUserRolesUrl = `/api/roles/user/${userId}`;
            request(getUserRolesUrl, { credentials: "same-origin" }, dispatch)
                .then(h.checkStatus)
                .then(toJson)
                .then((data: { roles: Array<IRole> }) => {
                    setUserRoles(data.roles);
                })
                .catch(error => {
                    h.error("Error loading User Roles.", error);
                });

            const getUserSitesUrl = "/UsersAdmin/GetUserSites?" + h.serialize({ userId });
            request(getUserSitesUrl, { credentials: "same-origin" }, dispatch)
                .then(h.checkStatus)
                .then(toJson)
                .then((data: { userSites: Array<ISite> }) => {
                    setUserSites(data.userSites);
                })
                .catch(error => {
                    h.error("Error loading User Sites.", error);
                });

            const getUserFeaturesUrl = "/Features/GetUserFeatures?" + h.serialize({ userId });
            request(getUserFeaturesUrl, { credentials: "same-origin" }, dispatch)
                .then(h.checkStatus)
                .then(toJson)
                .then((data: { userFeatures: Array<IFeature> }) => {
                    setUserFeatures(data.userFeatures);
                })
                .catch(error => {
                    h.error("Error loading User Features.", error);
                });
        }
    }, [dispatch, adminRoles, userId]);

    const saveNewUser = (val: boolean) => {
        if (validate()) {
            //dispatch(requestUserSave(userId, userSites, userRoles, userFeatures, isTemporaryAccount));
            dispatch(
                addNewReassignedUser(
                    localNewUser.Email,
                    localNewUser.FirstName,
                    localNewUser.LastName,
                    currentCompanyId
                )
            );
            dispatch(requestNewUserBeforeReassign(userId, userSites, userRoles, userFeatures));

            setSelectedUser(currentReassignedUser);
            const defaultAction: UserDeleteActionData = {
                IsReassignedTo: true,
                ReassignedFolderName: newFolderName,
                IsShowPrimaryButton: true,
            };
            onChangeImportAction(defaultAction);
            resetPage();
            setEditNewUserMode(val);
        }
    };

    const resetPage = () => {
        setLocalNewUser({ ...defaultNewUser });
    };

    const validate = () => {
        let errors: IPageErrors = {};

        if (!localNewUser.Email || localNewUser.Email.trim() == "") {
            errors.Email = "* New User email is required.";
        } else {
            const existEmail = users.find(user => user.Email == localNewUser.Email);
            if (existEmail) {
                errors.Email = "* Email is existed.";
            }
        }

        if (!localNewUser.FirstName || localNewUser.FirstName.trim() == "") {
            errors.FirstName = "* New First Name is required.";
        } else if (localNewUser.FirstName.length < 2) {
            errors.FirstName = "* New First Name must be at least 2 characters long.";
        }
        if (!localNewUser.LastName || localNewUser.LastName.trim() == "") {
            errors.LastName = "* New Last Name is required.";
        } else if (localNewUser.LastName.length < 2) {
            errors.LastName = "* New Last Name must be at least 2 characters long.";
        }

        setErrors(errors);
        return Object.keys(errors).length == 0;
    };

    const renderErrors = () => {
        let messages: any[] = [];
        if (!errors) {
            return null;
        }

        if (errors.Email) {
            messages.push(errors.Email);
        }
        if (errors.FirstName) {
            messages.push(errors.FirstName);
        }
        if (errors.LastName) {
            messages.push(errors.LastName);
        }

        const allErrors = computeErrors();
        const renderedCompaniesWithNoSeats = renderCompaniesWithNoSeatsErrors(allErrors);

        if (messages.length <= 0 && !renderedCompaniesWithNoSeats) {
            return null;
        }

        return (
            <div className="alert alert-warning-original flow-item-warnings">
                {messages.map((err, index) => (
                    <div key={index}>
                        <Icon style={{ marginRight: "10px", position: "relative", top: "2px" }}>error_outline</Icon>
                        {err}
                    </div>
                ))}
                {renderedCompaniesWithNoSeats}
            </div>
        );
    };

    const computeErrors = () => {
        //user errors returned from server side validation
        const userErrors = user.errors || {};
        return Object.assign({}, errors, userErrors);
    };

    const renderCompaniesWithNoSeatsErrors = errors => {
        if (!errors.companiesWithNoSeats || errors.companiesWithNoSeats.length <= 0) {
            return null;
        }
        let content = errors.companiesWithNoSeats.map(x => renderCompanyWithNoSeats(x));
        return (
            <div className="row">
                <div className="col-sm-12">
                    <div className="form-group has-error">
                        <span className="help-block">
                            * The following companies have no seats remaining and could not be added.
                        </span>
                        {content}
                    </div>
                </div>
            </div>
        );
    };

    const renderCompanyWithNoSeats = companyId => <li key={companyId}>{selectedCompany?.toString}</li>;

    const renderUsersSelect = () => {
        const filteredUsers = users.filter(x => x.Id != userId);
        return (
            <>
                <div>
                    {isSaving ? <CircularLoading size="large" color="#DDD" /> : null}
                    <FormControl style={{ minWidth: "25vw", paddingRight: "10px", marginTop: "10px" }}>
                        <InputLabel id="select-label">Reassign items to</InputLabel>
                        <Select
                            //labelid="select-label"
                            onChange={e => handleSettingValue(e.target.value)}
                            value={selectedUser || ""}
                            disabled={editNewUserMode || isSaving}
                        >
                            <MenuItem disabled value="">
                                <em>Select...</em>
                            </MenuItem>
                            {filteredUsers.map(u => renderUsersSelectOption(u))}
                        </Select>{" "}
                        Or
                        <Button
                            id="add-new-user"
                            variant="contained"
                            color="secondary"
                            startIcon={<i className="material-icons">add</i>}
                            onClick={() => {
                                setEditNewUserMode(true);
                                const defaultAction: UserDeleteActionData = {
                                    IsReassignedTo: true,
                                    ReassignedFolderName: newFolderName,
                                    IsShowPrimaryButton: false,
                                };
                                onChangeImportAction(defaultAction);
                            }}
                            disabled={editNewUserMode || isSaving}
                        >
                            Add New User
                        </Button>
                        <div className="m-t">
                            {editNewUserMode && (
                                <div
                                    className="service-layout-builder"
                                    style={{
                                        marginTop: "20px",
                                        width: "100%",
                                        border: "1pt solid lightgrey",
                                        borderRadius: "10px",
                                    }}
                                >
                                    <div style={{ padding: "10px" }}>
                                        <p>New User</p>
                                        <div>{renderErrors()}</div>
                                        <div>
                                            <TextField
                                                id="user-email"
                                                label="New User Email"
                                                type="text"
                                                value={localNewUser.Email}
                                                inputProps={{ maxLength: "256" }}
                                                fullWidth
                                                error={errors.Email != null}
                                                onChange={e => {
                                                    if (e.target.value.length > 0) {
                                                        setErrors({ ...errors, Email: "" });
                                                    }
                                                    setLocalNewUser({ ...localNewUser, Email: e.target.value });
                                                }}
                                                onWheel={event => {
                                                    const eventTarget = event.target as HTMLInputElement;
                                                    return eventTarget.blur();
                                                }}
                                            />
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <TextField
                                                id="user-first-name"
                                                label="New User First Name"
                                                type="text"
                                                style={{ width: "45%" }}
                                                value={localNewUser.FirstName}
                                                error={errors.FirstName != null}
                                                onChange={e => {
                                                    if (e.target.value.length > 0) {
                                                        setErrors({ ...errors, FirstName: "" });
                                                    }
                                                    setLocalNewUser({ ...localNewUser, FirstName: e.target.value });
                                                }}
                                            />
                                            <TextField
                                                id="user-last-name"
                                                label="New User Last Name"
                                                type="text"
                                                style={{ width: "45%" }}
                                                value={localNewUser.LastName}
                                                error={errors.LastName != null}
                                                onChange={e => {
                                                    if (e.target.value.length > 0) {
                                                        setErrors({ ...errors, LastName: "" });
                                                    }
                                                    setLocalNewUser({ ...localNewUser, LastName: e.target.value });
                                                }}
                                            />
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "right", marginTop: "20px" }}>
                                            <Button
                                                id="save-layout"
                                                variant="contained"
                                                color="primary"
                                                className={"edit-button"}
                                                style={{ minWidth: "fit-content", marginRight: "20px" }}
                                                startIcon={<i className="material-icons">save</i>}
                                                onClick={() => {
                                                    saveNewUser(false);
                                                }}
                                                disabled={isSaving}
                                            >
                                                <div className="btn-saving-context" style={{ padding: "0px" }}>
                                                    {isSaving ? (
                                                        <CircularLoading size="x-small" color="#DDD" saved={false} />
                                                    ) : null}
                                                    <span>{isSaving ? "Saving..." : "Save New User"}</span>
                                                </div>
                                            </Button>
                                            <Button
                                                id="cancel"
                                                variant="contained"
                                                color="secondary"
                                                className={"edit-button"}
                                                style={{
                                                    backgroundColor: "#cf4e4e",
                                                    padding: "0px 20px",
                                                    minWidth: "fit-content",
                                                }}
                                                startIcon={<i className="material-icons">cancel</i>}
                                                onClick={() => {
                                                    if (currentReassignedUser != 0) {
                                                        const defaultAction: UserDeleteActionData = {
                                                            IsReassignedTo: true,
                                                            ReassignedFolderName: newFolderName,
                                                            IsShowPrimaryButton: true,
                                                        };
                                                        onChangeImportAction(defaultAction);
                                                    }
                                                    setEditNewUserMode(!editNewUserMode);
                                                }}
                                                disabled={isSaving}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {selectedUser > 0 && (
                            <div>
                                <TextField
                                    inputProps={{ style: { fontSize: "15px" } }}
                                    label="Folder for Reassigned Items"
                                    type="text"
                                    style={{
                                        padding: "20px",
                                        width: "100%",
                                        border: "10pt solid #EEE",
                                        borderRadius: "10px",
                                    }}
                                    onFocus={handleFocus}
                                    value={newFolderName || ""}
                                    helperText={"Leave blank if you do not want a new folder"}
                                    onChange={e => setNewFolderName(e.target.value)}
                                />
                            </div>
                        )}
                    </FormControl>
                </div>
            </>
        );
    };

    const renderUsersSelectOption = (user: User) => {
        const optionText =
            (user.FirstName || "") + " " + (user.LastName || "") + " (" + (user.Email || "") + ") (" + user.Id + ")";
        return (
            <MenuItem key={user.Id} value={user.Id}>
                {optionText}
            </MenuItem>
        );
    };

    return (
        <div>
            {reassignMode && (
                <div style={{ marginBottom: "20px" }}>
                    <h4 style={{ paddingLeft: "5px", margin: "5px 0px", color: "#000" }}>
                        <strong>Reassign Items</strong>
                    </h4>
                    <Divider style={{ marginBottom: "5px" }} />
                    <div style={{ paddingLeft: "16px" }}>
                        {reassignMenu && (
                            <>
                                <span style={{ fontWeight: 500, marginRight: "10px", fontSize: "12px" }}>Method</span>
                                <RadioButton
                                    id="relative-or-absolute"
                                    name="RelOrAbs"
                                    options={[
                                        { label: "Yes", value: "yes" },
                                        { label: "No", value: "no" },
                                    ]}
                                    onChange={e => handleReassignValue(e.target.value)}
                                    row={true}
                                    valChecked={reassignMenu}
                                />

                                <div className={reassignMenu == "no" ? "hidden" : ""}>
                                    <div style={{ marginLeft: "auto", marginRight: 10, display: "flex" }}>
                                        {renderUsersSelect()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDelete;
